import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { formatDate } from "../shared/DateField";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QRCheckInPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState(null);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid QR code - no token found');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);

      // Decode JWT to get registration info (no API call needed for display)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      
      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        setValidationResult({
          isValid: false,
          message: 'This pass has expired',
          invalidReason: 'expired'
        });
        setLoading(false);
        return;
      }

      // Extract registration details from JWT
      const regData = {
        id: payload.registrationId,
        eventId: payload.eventId,
        companyId: payload.companyId,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        registrationType: payload.registrationType,
        passSerialNumber: payload.passSerial
      };

      setRegistration(regData);

      // Fetch event details (public endpoint)
      const eventResponse = await axios.get(
        `${API_URL}/api/Event/${payload.eventId}`
      );
      setEvent(eventResponse.data);

      setValidationResult({
        isValid: true,
        message: 'Valid event pass',
        registration: regData
      });

      setLoading(false);
    } catch (err) {
      console.error('Validation error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to validate pass');
      setValidationResult({
        isValid: false,
        message: 'Invalid or expired pass',
        invalidReason: 'error'
      });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Validating Pass</h2>
          <p className="text-gray-600">Please wait...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Invalid Pass</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
        {/* Valid Pass */}
        <div className="text-center mb-8">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Valid Event Pass</h1>
          <p className="text-gray-600">This pass is ready for check-in</p>
        </div>

        {/* Event Details */}
        {event && (
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 mb-6 text-white">
            <h2 className="text-2xl font-bold mb-2">{event.title}</h2>
            <div className="flex items-center gap-2 text-blue-100">
              <Clock className="w-5 h-5" />
              <span>
                {formatDate(String(event.startDate).slice(0, 10), 'en', event.calendarPreference, event.hijriAdjustmentDays || 0)}
              </span>
            </div>
            {event.location && (
              <p className="mt-2 text-blue-100">📍 {event.location}</p>
            )}
          </div>
        )}

        {/* Registration Details */}
        {registration && (
          <div className="space-y-4 mb-6">
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-semibold text-gray-700 mb-3">Pass Holder Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium text-gray-800">
                    {registration.firstName} {registration.lastName}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-800">{registration.email}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Pass Type</p>
                  <p className="font-medium text-gray-800 capitalize">
                    {registration.registrationType || 'Regular'}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Serial Number</p>
                  <p className="font-medium text-gray-800 font-mono text-sm">
                    {registration.passSerialNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-900 mb-2">📱 Check-In Instructions</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Present this QR code at the event entrance</li>
            <li>• Event staff will scan your pass for check-in</li>
            <li>• You can also use your digital wallet pass</li>
            <li>• Keep this pass accessible during the event</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => window.print()}
            className="bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
          >
            Print Pass
          </button>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Done
          </button>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-gray-500 mt-6">
          If you have any questions, please contact the event organizer
        </p>
      </div>
    </div>
  );
};

export default QRCheckInPage;
